import {
  ExpiryType,
  getFormatter,
  isDefined,
  isListedInstrumentTradableInApp,
  orderTypesWhenPriceRequired,
  orderTypesWhenStopActivationPriceRequired,
  TradeType,
  useDictionary,
  useOrderExchangeCountdownCommon,
} from '@eih-netbroker/common';
import { useCallback } from 'react';
import { ReactComponent as DownloadIcon } from '../../../../../assets/icons/download.svg';
import { ReactComponent as LinkIcon } from '../../../../../assets/icons/link.svg';
import { useLocale } from '../../../../../context/NbLocalizationContext';
import { NbButton } from '../../../../../shared/components/button/Button';
import { CountdownBar } from '../../../../../shared/components/countdown-bar/CountdownBar';
import { getRemainingSecondsLabel } from '../../../../../shared/components/countdown-bar/countdownBarUtils';
import { NbTooltip } from '../../../../../shared/components/tooltip/Tooltip';
import { useAccounts } from '../../../../../shared/services/useAccounts';
import { cssWhen } from '../../../../../shared/utils/themeUtils';
import { useCreateNormalOrder, ValidatedOrder } from '../../../services/order/useNormalOrderView';
import { CapturedExchangeData } from '../../../types/orderTypes';
import classes from './OrderConfirm.module.scss';
import { OrderConfirmBlock, OrderConfirmRow } from './OrderConfirmComponents';
import { OrderConfirmCostBlock } from './OrderConfirmCostBlock';
import { OrderConfirmExchangeBlock } from './OrderConfirmExchangeBlock';
import { OrderConfirmInstrumentInfo } from './OrderConfirmInstrumentInfo';

type OrderConfirmProps = {
  orderForm: ValidatedOrder;
  capturedExchangeData?: CapturedExchangeData;
  onBack: (shouldResetFormToDefaults: boolean) => void;
};

export const OrderConfirm = ({ orderForm, capturedExchangeData, onBack }: OrderConfirmProps) => {
  const { translation } = useLocale();
  const { formatNumberF2, formatDate, formatInteger } = getFormatter();
  const { formatDictionaryType } = useDictionary();
  const { selectedAccountOrClient } = useAccounts();
  const { downloadPdfFileForNormalOrder, pdfDownloading } = useCreateNormalOrder();

  const handleOnExchangeRateExpired = useCallback(() => onBack(false), [onBack]);

  const { countdown } = useOrderExchangeCountdownCommon({
    exchangeRate: capturedExchangeData?.exchangeRate,
    onExchangeRateExpired: handleOnExchangeRateExpired,
  });
  const t = translation.newOrderConfirm;

  const account = selectedAccountOrClient.account;
  const accountValue = account
    ? `${formatDictionaryType(account.accountCategory, 'accountCategoryTypes')} - ${account.clientId}`
    : undefined;

  const orderTypeValue = orderForm.orderType ? formatDictionaryType(orderForm.orderType, 'orderTypes') : undefined;
  const tradeTypeValue = translation.orderConfirm.rows.tradeTypeValues[orderForm.tradeType ?? TradeType.Buy];

  const limitPriceValue = isDefined(orderForm.price)
    ? `${formatNumberF2(orderForm.price)} ${orderForm.instrument?.currency ?? ''}`
    : undefined;

  const activationPriceValue = isDefined(orderForm.stopActivationPrice)
    ? `${formatNumberF2(orderForm.stopActivationPrice)} ${orderForm.instrument?.currency ?? ''}`
    : undefined;

  const quantityValue = `${formatInteger(orderForm.quantity ?? 0)} ${translation.orderModal.quantityEndorment}`;

  const validityValue = orderForm.expiryType
    ? orderForm.expiryType === ExpiryType.Gtd && orderForm.expiry
      ? formatDate(orderForm.expiry)
      : formatDictionaryType(orderForm.expiryType, 'orderExpiryTypes')
    : undefined;

  const tradingSessionValue = orderForm.tradingSession
    ? formatDictionaryType(orderForm.tradingSession, 'tradingSessions')
    : undefined;

  const tradeable = isListedInstrumentTradableInApp(orderForm.instrument);
  const showInventory = orderForm.tradeType === TradeType.Sell && tradeable;

  const handleOnExportClick = useCallback(() => {
    downloadPdfFileForNormalOrder(orderForm);
  }, [orderForm, downloadPdfFileForNormalOrder]);

  const showCountdown = isDefined(countdown) && countdown !== 'indefinite';

  return (
    <div className={classes.mainContainer}>
      <OrderConfirmInstrumentInfo orderForm={orderForm} />
      <OrderConfirmBlock>
        <OrderConfirmRow
          label={orderForm.tradeType === TradeType.Buy ? t.accountBuy : t.accountSell}
          value={accountValue}
          testIdBase="order-confirm-account-name"
        />
        <div className={classes.rowContainer}>
          <span
            data-testid="order-confirm-trade-type-label"
            className={classes.label}
          >
            {t.orderType}
          </span>
          <div className={classes.rowContainer}>
            <span
              data-testid="order-confirm-trade-type"
              className={cssWhen(
                classes.tradeTypeContainer,
                [orderForm.tradeType === TradeType.Buy, classes.buyContainer],
                [orderForm.tradeType === TradeType.Sell, classes.sellContainer],
              )}
            >
              {tradeTypeValue}
            </span>
            <span
              className={classes.value}
              data-testid="order-confirm-order-type"
            >
              {orderTypeValue}
            </span>
          </div>
        </div>
        <div className={classes.divider} />
        <OrderConfirmRow
          label={t.quantity}
          value={quantityValue}
          testIdBase="order-confirm-quantity"
        />
        {orderTypesWhenPriceRequired.includes(orderForm.orderType) && (
          <OrderConfirmRow
            label={t.limitPrice}
            value={limitPriceValue}
            testIdBase="order-confirm-price"
          />
        )}
        {orderTypesWhenStopActivationPriceRequired.includes(orderForm.orderType) && (
          <OrderConfirmRow
            label={t.activationPrice}
            value={activationPriceValue}
            testIdBase="order-confirm-stop-activation-price"
          />
        )}
      </OrderConfirmBlock>

      <OrderConfirmCostBlock orderForm={orderForm} />

      <OrderConfirmExchangeBlock
        orderForm={orderForm}
        capturedExchangeData={capturedExchangeData}
      />

      <OrderConfirmBlock>
        <OrderConfirmRow
          label={t.validity}
          value={validityValue}
          testIdBase="order-confirm-expiry-type"
        />
        <OrderConfirmRow
          label={t.tradingSession}
          value={tradingSessionValue}
          testIdBase="order-confirm-trading-session"
        />
        {showInventory && (
          <OrderConfirmRow
            label={t.stockValuation}
            value={orderForm.inventoryManagementMethod}
            testIdBase="order-confirm-stock-valuation"
          />
        )}
      </OrderConfirmBlock>

      <div className={cssWhen(classes.rowConatinerButton, [!showCountdown, classes.extraBottomPadding])}>
        <NbButton
          onClick={handleOnExportClick}
          data-testid="order-confirm-preliminary-expenses-button"
          isLoading={pdfDownloading}
          className={classes.buttonContainer}
          variant="text"
          size="small"
          startIcon={<DownloadIcon className={classes.icon} />}
        >
          {translation.newOrderConfirm.preliminaryExpenses}
        </NbButton>
        <NbTooltip
          title={translation.general.KIDTooltip}
          placement="top"
        >
          <NbButton
            onClick={() => window.open('https://kid.ersteinvestment.hu/', '_blank', 'noopener,noreferrer')}
            data-testid="order-confirm-kid-document-link"
            className={classes.buttonContainer}
            variant="text"
            size="small"
            startIcon={<LinkIcon className={classes.icon} />}
          >
            {translation.general.KIDDocumentSearch}
          </NbButton>
        </NbTooltip>
      </div>

      {showCountdown && (
        <div className={classes.countdownContainer}>
          <CountdownBar
            shouldUseCountdownBarPlaceholder={false}
            max={countdown.durationSeconds}
            remaining={countdown.secondsRemaining}
            getLabel={getRemainingSecondsLabel}
          />
        </div>
      )}
    </div>
  );
};
